import React, { useEffect, useState } from 'react';
import { Loader2, FolderPlus } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';

interface NewFolderDialogProps {
  open: boolean;
  /** Name of the parent folder the new folder will be created inside (undefined => root). */
  parentName?: string;
  isSaving?: boolean;
  onCancel: () => void;
  onSave: (name: string) => void;
}

export default function NewFolderDialog({
  open,
  parentName,
  isSaving = false,
  onCancel,
  onSave,
}: NewFolderDialogProps) {
  const [value, setValue] = useState('');

  // Re-seed the input each time the dialog opens.
  useEffect(() => {
    if (open) setValue('');
  }, [open]);

  const handleSave = () => {
    if (isSaving) return;
    const trimmed = value.trim();
    if (!trimmed) {
      toast({
        title: 'שם התיקייה לא יכול להיות ריק',
        variant: 'destructive',
        style: { direction: 'rtl' },
      });
      return;
    }
    onSave(trimmed);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <DialogContent dir="rtl" className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-right flex items-center gap-2">
            <FolderPlus className="w-5 h-5" />
            תיקייה חדשה
          </DialogTitle>
        </DialogHeader>

        {parentName && (
          <p className="text-sm text-muted-foreground text-right">
            בתוך: <span className="font-medium text-foreground">{parentName}</span>
          </p>
        )}

        <Input
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSave();
            }
          }}
          placeholder="שם התיקייה"
          className="text-right"
          disabled={isSaving}
        />

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={isSaving}>
            ביטול
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving && <Loader2 className="w-4 h-4 animate-spin ml-2" />}
            צור
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
